import { useState, useEffect } from 'react';

import { getCaseObjectFromComment } from '../utils/getCaseObjectFromComment';
import {
	validateActivitySelection,
	validateCaseSelection,
	validateDescription,
	validateEntryDate,
	validateRegularAndOverTime,
	validateTitle,
	ValidatorFunction,
} from '../utils/validateRegularAndOverTime';
import { CANtrackCase, CANtrackComment } from '../types/comment';
import useSaveComment from './useSaveComment';
import { useNotificationProvider } from 'hooks';
import { useLoading } from 'contextProviders/LoadingBackdropProvider';
import { deleteComment } from 'api/comments/deleteComment';
import getUpdatedCommentFromFormState from '../utils/getUpdatedCommentFromFormState';
import { GPDActivity } from './useActivities';

export interface CommentFormState {
	title: string;
	description: string;
	selectedCase: CANtrackCase | null;
	selectedActivity: GPDActivity | null;
	entryDate: Date;
	time: {
		regularTime: number;
		overTime: number;
	};
	billable: boolean;
}
export interface CommentFormStateErrors {
	title?: string;
	description?: string;
	selectedCase?: string;
	selectedActivity?: string;
	entryDate?: string;
	time?: string;
	billable?: string;
}

function useCommentEditor(
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>,
	initialComment?: CANtrackComment,
	userId?: string,
	contactId?: string,
	defaultCase?: CANtrackCase | null,
) {
	// State management
	const [formState, setFormState] = useState<CommentFormState>(
		getDefaultFormState(),
	);
	const [errors, setErrors] = useState<CommentFormStateErrors>({});

	// Supporting hooks
	const { saveComment } = useSaveComment(setComments);
	const { notifySuccess, notifyError } = useNotificationProvider();
	const { setIsLoading } = useLoading();

	// Helper functions
	function getDefaultFormState(): CommentFormState {
		// Define default form state based on existing comment or default values
		return {
			title: initialComment?.title || '',
			description: initialComment?.description || '',
			selectedCase: initialComment
				? getCaseObjectFromComment(initialComment)
				: defaultCase || null,
			selectedActivity: initialComment?.gpda
				? {
						Id: initialComment.gpda.activity.id.toString(),
						Title: initialComment.gpda.activity.title,
				  }
				: null,
			entryDate: initialComment?.date || new Date(),
			time: {
				regularTime: initialComment?.timeRegular || 0.1,
				overTime: initialComment?.timeOver || 0,
			},
			billable: initialComment ? initialComment.billable : true,
		};
	}

	// CRUD operations
	async function handleDeleteComment() {
		if (!initialComment) return;
		setIsLoading(true);
		try {
			await deleteComment(initialComment.id);
			setComments((prev) =>
				prev.filter((c) => c.id !== initialComment.id),
			);
			notifySuccess('Comment deleted successfully');
		} catch (error) {
			console.error('Error deleting comment:', error);
			notifyError('Error deleting comment');
		} finally {
			setIsLoading(false);
		}
	}

	async function handleSaveComment(): Promise<boolean> {
		if (!validateForm()) {
			notifyError('Please fix the highlighted fields before saving');
			return false;
		}
		setIsLoading(true);
		try {
			await saveComment(
				getUpdatedCommentFromFormState(
					formState,
					initialComment,
					userId,
					contactId,
				),
			);
			notifySuccess('Comment saved successfully');
			handleResetComment();
			return true;
		} catch (error) {
			console.error('Error saving comment:', error);
			notifyError('Error saving comment');
			return false;
		} finally {
			setIsLoading(false);
		}
	}

	// Form handling
	const validateForm = (): boolean => {
		const newErrors: CommentFormStateErrors = {
			title: validateTitle(formState.title),
			description: validateDescription(formState.description),
			selectedCase: validateCaseSelection(formState.selectedCase),
			selectedActivity: validateActivitySelection(
				formState.selectedActivity,
			),
			entryDate: validateEntryDate(formState.entryDate),
			time: validateRegularAndOverTime(formState.time),
			billable: undefined, // No validation needed for billable
		};

		// Remove undefined values from newErrors
		Object.keys(newErrors).forEach((key) => {
			if (newErrors[key as keyof CommentFormStateErrors] === undefined) {
				delete newErrors[key as keyof CommentFormStateErrors];
			}
		});

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle input changes
	const handleChange = <T extends keyof CommentFormState>(
		field: T,
		value: CommentFormState[T],
		performValidation = true,
	) => {
		setFormState((prev: CommentFormState) => ({ ...prev, [field]: value }));
		if (performValidation) validateField(field, value);
	};

	const validateField = (name: keyof CommentFormStateErrors, value: any) => {
		const validators: Record<
			keyof CommentFormStateErrors,
			ValidatorFunction<any>
		> = {
			title: validateTitle,
			description: validateDescription,
			selectedCase: validateCaseSelection,
			selectedActivity: validateActivitySelection,
			entryDate: validateEntryDate,
			time: validateRegularAndOverTime,
			billable: () => undefined, // No validation needed for billable
		};

		const error = validators[name](value);
		setErrors((prev: any) => ({
			...prev,
			[name]: error,
		}));
	};

	function handleResetComment() {
		setFormState(getDefaultFormState());
		setErrors({});
	}

	useEffect(() => {
		setFormState(getDefaultFormState());
	}, [initialComment]);

	return {
		formState,
		errors,
		handleChange,
		handleDeleteComment,
		handleSaveComment,
		handleResetComment,
	};
}

export default useCommentEditor;
